import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { WithDisabledFlags } from "shared/types/WithDisabledFlags"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { TeamActionHandlers } from "../types/TeamActionHandlers"

interface UseTeamActionsArgs
  extends Partial<TeamActionHandlers>,
    WithDisabledFlags<TeamActionHandlers> {
  idPrefix: string
  isReadonly?: boolean
  teamID: string
}

export function useTeamActions({
  teamID,
  idPrefix,
  isReadonly = false,
  handleDeleteTeam,
  isDeleteTeamDisabled = false
}: UseTeamActionsArgs) {
  const { t } = useTranslation()

  const deleteAction: Action = useMemo(
    () => ({
      id: `${idPrefix}__team__actions__delete`,
      "data-testid": "team__actions__delete",
      icon: IconName.BIN,
      label: t("Team.Actions.Delete"),
      isDanger: true,
      disabled: isDeleteTeamDisabled,
      onClick: handleDeleteTeam
        ? () => handleDeleteTeam?.({ id: teamID })
        : undefined
    }),
    [t, isDeleteTeamDisabled, handleDeleteTeam]
  )

  const allActions = useMemo<Action[]>(() => [deleteAction], [deleteAction])

  const readonlyActions = useMemo<Action[]>(() => [], [])

  const actions = useMemo<Action[]>(
    () =>
      (isReadonly ? readonlyActions : allActions).filter(
        action => !!action.onClick
      ),
    [allActions, readonlyActions, isReadonly]
  )

  return {
    actions,
    deleteAction
  }
}
